import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { pickInitialSeasonId, saveSeasonPref } from "@/lib/seasonPref";
import { computeRanks } from "@/lib/leaderboardRanks";
import {
  Trophy, Camera, Layers, Activity,
  Settings as SettingsIcon, FileDown, Palette,
} from "lucide-react";

/**
 * Season Stats — club-wide season summary.
 *
 * Layout: a season-selector + Export PDF button up top, then a row of
 * three "single figure" tiles (Total Entries, Average Mark, Average
 * Entries per Competition), then a Total Points top-5 leaderboard that
 * still honours the scoring rule toggle (Inherit / Best / Top 2 / All).
 *
 * The three single-figure tiles describe participation + raw judging;
 * the toggle doesn't affect them. Total Points is the only score-based
 * tile, so it sits with the toggle controls.
 */

const SCORING_OPTIONS = [
  { value: "inherit", short: "Inherit", long: "Follow club's Scoring rules in Settings" },
  { value: "top1",    short: "Best",    long: "Best image per round" },
  { value: "top2",    short: "Top 2",   long: "Top 2 images per round" },
  { value: "all",     short: "All",     long: "Every entry counts" },
];
const RESOLVED_LABEL = { all: "every entry", top1: "best image", top2: "top 2" };


export default function SeasonStats() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState("");
  const [summary, setSummary] = useState(null);
  const [topPoints, setTopPoints] = useState([]);

  const [scoring, setScoring] = useState(
    () => localStorage.getItem("ccs_leaderboard_scoring") || "inherit"
  );
  const [clubScoring, setClubScoring] = useState("top2");
  const [clubLogo, setClubLogo] = useState("");
  const effectiveScoring = scoring === "inherit" ? clubScoring : scoring;

  useEffect(() => { localStorage.setItem("ccs_leaderboard_scoring", scoring); }, [scoring]);

  useEffect(() => {
    (async () => {
      const [s, c] = await Promise.all([
        api.get("/seasons"),
        api.get("/clubs/me").catch(() => ({ data: {} })),
      ]);
      setSeasons(s.data);
      setClubScoring(c.data?.poy_scoring_mode || "top2");
      setClubLogo(c.data?.logo_data_url || "");
      setSeasonId(pickInitialSeasonId(s.data));
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const params = {};
      if (seasonId) params.season_id = seasonId;
      const { data } = await api.get("/stats/season-summary", { params });
      setSummary(data);
    })();
  }, [seasonId]);

  useEffect(() => {
    (async () => {
      const params = { scoring: effectiveScoring, limit: 5 };
      if (seasonId) params.season_id = seasonId;
      const { data } = await api.get("/stats/leaderboard", { params });
      setTopPoints(computeRanks(data.slice(0, 5)));
    })();
  }, [seasonId, effectiveScoring]);

  const seasonName = seasons.find((s) => s.id === seasonId)?.season_name || "All seasons";
  const clubName = user?.club_name || "Camera Club";

  const exportPDF = () => {
    const doc = new jsPDF("p", "mm", "a4");
    let y = 18;
    // If the club has uploaded a logo, place it at the top-centre above
    // the club name. jsPDF's addImage tolerates data URLs directly so
    // no manual base64 juggling is needed.
    if (clubLogo) {
      try {
        // Auto-detect the image format from the data-URL header (JPG or
        // PNG). SVGs aren't natively supported by jsPDF so they'll be
        // silently skipped by the catch below and the PDF still renders.
        const fmt = /^data:image\/(png|jpeg|jpg|webp)/i.exec(clubLogo)?.[1]?.toUpperCase();
        if (fmt) {
          doc.addImage(clubLogo, fmt === "JPG" ? "JPEG" : fmt, 95, y - 6, 20, 20);
          y += 18;
        }
      } catch (_) {
        // If jsPDF can't parse the image (e.g. SVG), fall back to
        // text-only header — the PDF still renders cleanly.
      }
    }
    doc.setFont("helvetica", "bold").setFontSize(16);
    doc.text(clubName, 105, y, { align: "center" });
    y += 7;
    doc.setFontSize(13);
    doc.text("Season Stats", 105, y, { align: "center" });
    y += 6;
    doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(110);
    doc.text(seasonName, 105, y, { align: "center" });
    y += 4;
    doc.text(`Total Points scoring rule: ${RESOLVED_LABEL[effectiveScoring] || effectiveScoring}`, 105, y, { align: "center" });
    y += 8;

    doc.setTextColor(17);
    autoTable(doc, {
      startY: y,
      head: [["Headline figure", "Value"]],
      body: [
        ["Total Competitions",            summary?.total_competitions ?? 0],
        ["Total Entries",                 summary?.total_entries ?? 0],
        ["Average Mark (across every entry)", summary?.avg_mark ?? 0],
        ["Average Entries per Competition",   summary?.avg_entries_per_competition ?? 0],
      ],
      styles: { fontSize: 10, cellPadding: 2 },
      headStyles: { fillColor: [251, 191, 36], textColor: 30 },
      columnStyles: { 1: { halign: "right" } },
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      head: [["#", "Photographer", "Code", "Total Points"]],
      body: topPoints.map((r) => [
        r._tied ? `${r._rank} (T)` : r._rank,
        r.photographer_name || r.photographer_code || "—",
        r.photographer_code || "",
        r.total_score,
      ]),
      styles: { fontSize: 10, cellPadding: 2 },
      headStyles: { fillColor: [251, 191, 36], textColor: 30 },
      columnStyles: { 0: { cellWidth: 12 }, 3: { halign: "right", cellWidth: 30 } },
    });

    const fname = `season-stats-${(seasonName || "all").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`;
    doc.save(fname);
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl" data-testid="season-stats-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="ccs-label mb-2">Season summary</div>
          <h1 className="text-4xl font-semibold tracking-tight">Season Stats</h1>
          <div className="muted-text mt-1 text-sm">{seasonName}</div>
        </div>

        <div className="flex items-center gap-2">
          <select
            value={seasonId}
            onChange={(e) => { setSeasonId(e.target.value); saveSeasonPref(e.target.value); }}
            className="ccs-input text-sm"
            data-testid="season-stats-season-select"
          >
            {seasons.length === 0 && <option value="">All seasons</option>}
            {seasons.map((s) => (
              <option key={s.id} value={s.id}>
                {s.season_name}{s.is_active ? " (active)" : ""}
              </option>
            ))}
          </select>

          <button
            onClick={exportPDF}
            className="ccs-btn-ghost text-sm flex items-center gap-2"
            data-testid="season-stats-export-pdf"
            title="Download a PDF of this page"
          >
            <FileDown size={14} /> Export PDF
          </button>

          <TopScorersExport seasonId={seasonId} seasonName={seasonName} />
          <FirstPlacesExport seasonId={seasonId} seasonName={seasonName} />
          <button
            onClick={() => navigate(`/graphic-studio${seasonId ? `?season_id=${seasonId}` : ""}`)}
            className="ccs-btn text-sm flex items-center gap-2"
            data-testid="graphic-studio-button"
            title="Design and print certificates from this season's results"
          >
            <Palette size={14} /> Graphic Studio
          </button>
        </div>
      </div>

      {/* three single-figure tiles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <StatTile
          icon={Camera}
          label="Total Entries"
          value={summary?.total_entries ?? 0}
          hint={`${summary?.total_competitions ?? 0} competition${summary?.total_competitions === 1 ? "" : "s"}`}
          testId="stat-total-entries"
        />
        <StatTile
          icon={Activity}
          label="Average Mark"
          value={summary?.avg_mark ?? 0}
          hint="across every entry"
          testId="stat-avg-mark"
        />
        <StatTile
          icon={Layers}
          label="Average Entries"
          value={summary?.avg_entries_per_competition ?? 0}
          hint="per competition"
          testId="stat-avg-entries"
        />
      </div>

      {/* Total Points — the only score-sensitive card, so the toggle lives here */}
      <section className="ccs-card" data-testid="card-total-points">
        <div className="flex items-start gap-3 mb-4 flex-wrap">
          <Trophy className="text-amber-400 mt-0.5 shrink-0" size={20} />
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold">Total Points</h2>
            <p className="muted-text text-xs mt-0.5">Top 5 photographers by season total.</p>
          </div>

          <div className="flex items-center gap-2 flex-wrap" data-testid="season-stats-scoring-toggle">
            <span className="muted-text text-xs">Total based on:</span>
            <div className="inline-flex" style={{ background: "#0f0f0f", border: "1px solid #1f1f1f" }}>
              {SCORING_OPTIONS.map((o, i) => {
                const active = o.value === scoring;
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => setScoring(o.value)}
                    className="px-3 py-1 text-xs font-medium transition-colors"
                    style={{
                      background: active ? "#1d160a" : "transparent",
                      color: active ? "#fbbf24" : "#999",
                      borderLeft: i > 0 ? "1px solid #1f1f1f" : "none",
                    }}
                    title={o.long}
                    data-testid={`season-stats-scoring-${o.value}`}
                  >
                    {o.short}
                  </button>
                );
              })}
            </div>
            {scoring === "inherit" && (
              <span className="muted-text text-xs" data-testid="season-stats-scoring-resolved">
                → {RESOLVED_LABEL[clubScoring] || clubScoring}
              </span>
            )}
            {user?.role === "ADMIN" && (
              <Link
                to="/settings"
                className="muted-text text-xs hover:text-amber-400 inline-flex items-center gap-1"
                title="Change the club-wide scoring rule"
                data-testid="season-stats-configure-link"
              >
                <SettingsIcon size={11} /> Configure
              </Link>
            )}
          </div>
        </div>

        {topPoints.length === 0 ? (
          <div className="muted-text text-sm" data-testid="card-total-points-empty">
            No competitions have been scored yet. Once you record results in{" "}
            <Link to="/live" className="text-amber-400">Live Entry</Link>,
            the leaderboard will populate automatically.
          </div>
        ) : (
          <table className="ccs-table text-sm">
            <thead>
              <tr>
                <th style={{ width: 32 }}>#</th>
                <th>Photographer</th>
                <th style={{ width: 90 }}>Code</th>
                <th className="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {topPoints.map((r, i) => (
                <tr key={r.photographer_id} data-testid={`top-points-row-${i}`}>
                  <td className="muted-text mono">{r._rank}</td>
                  <td className="font-medium">
                    {r.photographer_name || r.photographer_code}
                    {r._tied && (
                      <span
                        className="ml-2 inline-block align-middle px-1.5 py-0.5 text-[9px] uppercase tracking-wider rounded-sm bg-amber-500/15 text-amber-300 border border-amber-500/30"
                        title="Tied on total score — countback rules apply for the final award"
                        data-testid="top-points-tied-pill"
                      >
                        Tied
                      </span>
                    )}
                  </td>
                  <td className="mono muted-text text-xs">{r.photographer_code}</td>
                  <td className="text-right mono font-medium">{r.total_score}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}


function StatTile({ icon: Icon, label, value, hint, testId }) {
  return (
    <div className="ccs-card" data-testid={testId}>
      <div className="flex items-start justify-between mb-3">
        <div className="ccs-label text-xs">{label}</div>
        <Icon className="text-amber-400 shrink-0" size={18} />
      </div>
      <div className="text-4xl font-semibold tracking-tight mono">{value}</div>
      <div className="muted-text text-xs mt-1">{hint}</div>
    </div>
  );
}


// Certificate-of-Merit mail-merge export — downloads every image entry
// in the currently-selected season that scored the competition's top
// mark. Spec (user, Feb 2026): "a club can have a top mark of 10 or a
// top mark of 20. Only those that achieve those top marks are in the
// CSV. A photographer could have 5 different images with the top mark;
// these should be separately in the CSV."
function TopScorersExport({ seasonId, seasonName }) {
  const download = async () => {
    if (!seasonId) return;
    try {
      const params = new URLSearchParams({ season_id: seasonId });
      const r = await api.get(`/export/top-scorers.csv?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const cd = r.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(cd);
      a.download = match ? match[1] : "top-scorers.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (ex) {
      toast?.error?.(ex.response?.data?.detail || ex.message || "Failed to download CSV");
    }
  };

  const disabled = !seasonId;

  return (
    <button
      onClick={download}
      disabled={disabled}
      className="ccs-btn-ghost text-sm flex items-center gap-2"
      data-testid="top-scorers-export-button"
      title={
        disabled
          ? "Select a season to enable"
          : `Download all top-mark scorers in ${seasonName} — for mail-merged Certificates of Merit`
      }
    >
      <FileDown size={14} /> Certificates CSV
    </button>
  );
}



// 1st-Places CSV — every entry in the season that was awarded 1st place.
// Identical column layout to the Top-Scorers export (Code, Name,
// Competition, Image Title, Score, Judge, Place) so it drops into the
// same mail-merge template — just filtered to "FIRST" placements
// instead of the per-competition top mark.
function FirstPlacesExport({ seasonId, seasonName }) {
  const download = async () => {
    if (!seasonId) return;
    try {
      const params = new URLSearchParams({ season_id: seasonId });
      const r = await api.get(`/export/first-places.csv?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const cd = r.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(cd);
      a.download = match ? match[1] : "first-places.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (ex) {
      toast?.error?.(ex.response?.data?.detail || ex.message || "Failed to download CSV");
    }
  };

  const disabled = !seasonId;

  return (
    <button
      onClick={download}
      disabled={disabled}
      className="ccs-btn-ghost text-sm flex items-center gap-2"
      data-testid="first-places-export-button"
      title={
        disabled
          ? "Select a season to enable"
          : `Download every 1st-place entry in ${seasonName} — same CSV shape as Certificates`
      }
    >
      <FileDown size={14} /> 1st Places CSV
    </button>
  );
}
